import React, {useEffect, useState} from "react";
import Resources from "../../../data/services/resources";
import {Field, FieldsManager} from "../../../data/services/fields";
import {
    checkPerm,
    classNames,
    getDefaultQueryFields,
    getDefaultTableOptions, getDefaultUserOfficeValues,
    getLookup,
    getProp,
    groupListBySCAC, longTableColumn,
    openInNewTab
} from "../../../common/util/util-helpers";
import {getUser} from "../../../common/util/util-auth";
import {useDispatch, useSelector} from "react-redux";
import {getResource} from "../../../data/actions/resource";
import useQuery from "../../../hooks/use-query";
import {
    CheckCircleIcon,
    ChevronUpIcon as ChevronUpSolidIcon,
    Cog6ToothIcon,
    EyeIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import {genericMoneyFormatter} from "../../../common/util/util-vanilla";
import {READ_PERM} from "../../../util/util-constants";
import {showGlobalModal} from "../../../data/actions/ui";
import Layout from "../../../common/components/layout";
import {fillFieldsFromData} from "../../../common/util/util-fields";
import ResourceTableTags from "../../../common/components/resource-table/table-components/resource-table-tags";
import CellButton from "../../../common/components/resource-table/table-components/cell-button";
import ResourceList from "../../../common/components/layout/layout-components/resource-list";
import TableBulkActions from "../../../common/components/resource-table/table-components/table-bulk-actions";
import TableTagManager from "../../../common/components/resource-table/table-components/table-tag-manager";
import PageHeader from "../../../common/components/layout/layout-components/page/page-header";
import WorkOrderStatusBadge from "../../../common/components/badge/work-order-status-badge/index";
import Page from "../../../common/components/layout/layout-components/page";
import Tippy from "@tippyjs/react";
import {CREATE_PERM} from "../../../common/util/util-consts";
import Env from "../../../util/env";
import WOPayProcessBill from "./wo-pay-process-bill";

const CheckboxCell = ({value}) => {
    return value ? <CheckCircleIcon className="w-5 h-5 text-green-600 inline"/>
        : <XMarkIcon className="w-5 h-5 text-tm-gray-400 inline"/>
}
export default function WorkOrderPay({history, match, translate}) {
    /** Store
     ================================================================= */

    const dispatch = useDispatch();
    const resource = useSelector((state) => state.resource);
    const isLoading = getProp(resource, "isLoading", true);

    const [selectedRows, setSelectedRows] = useState({});
    const [selectedItem, setSelectedItem] = useState(null);
    const [isProcessBillModalOpen, setIsProcessBillModalOpen] = useState(false);
    const workOrderStatuses = getLookup("WorkOrderStatus");

    const totalAmountSelected = Object.values(selectedRows).reduce((memo, it) => {
        const amount = parseFloat(it?.TotalAmount);
        return memo + (isNaN(amount) ? 0 : amount);
    }, 0);

    const roundedTotalAmount = Number(totalAmountSelected.toFixed(2));
    /** State
     ================================================================= */
    const [queryFields, setQueryFields] = useQuery(getQueryFields(translate), getListPath());
    const query = Object.assign({}, FieldsManager.getFieldKeyValues(queryFields), {});

    const [tableOptions, setTableOptions] = useState(
        getDefaultTableOptions(getFields(), {
            behaviour: {
                rowSelect: true,
                hasMenu: true,
                canAdjustWidth: true
            }
        }, getListPath(), translate)
    );

    useEffect(() => {
        fetchData(dispatch, query);
    }, [queryFields]);

    /** Data events
     ================================================================= */
    const fetchData = () => {
        dispatch(getResource({
            user: getUser(),
            resource: getResourcePath(),
            query: query
        }))
    }

    /** UI events
     ================================================================= */
    const handleViewResourceClick = (item) => {
        openInNewTab(Env.getTruckWorkOrdersUrl(`/work-orders/${item.WorkOrderID}`))

    }

    const handleSelectRowClick = (item) => {
        const itemID = item.WorkOrderID

        let selectedRowsUpdate = Object.assign({}, selectedRows);

        if (selectedRowsUpdate[itemID]) {
            delete selectedRowsUpdate[itemID]
        } else {
            Object.assign(selectedRowsUpdate, {[itemID]: item})
        }

        setSelectedRows(selectedRowsUpdate)
    }

    const handleSelectAllClick = (selectAll) => {
        const data = getProp(resource.data, 'list', []);

        let selectedRowsClone = Object.assign({}, selectedRows);

        if (!selectAll) {
            Object.assign(selectedRowsClone, data.reduce((memo, it) => {
                memo[it.WorkOrderID] = it
                return memo
            }, {}))
        } else {
            let selectedRowsCloneKeys = Object.keys(selectedRowsClone)
            data.forEach(it => {
                if (selectedRowsCloneKeys.includes(it.WorkOrderID.toString())) {
                    delete selectedRowsClone[it.WorkOrderID]
                }
            })
        }

        setSelectedRows(selectedRowsClone)
    }

    const handleViewTruckClick = (truckID) => {
        dispatch(showGlobalModal('ViewTruckCard', truckID));
    }

    const handleViewTrailerClick = (trailerID) => {
        dispatch(showGlobalModal('ViewTrailerCard', trailerID));
    }


    const handleProcessBillClick = (item) => {
        setSelectedItem(item);
        setIsProcessBillModalOpen(true);
    }

    /** Helpers
     ================================================================= */
    function getFields(item = {}) {
        const TruckID = !!item?.TruckID;
        const TrailerID = !!item?.TrailerID;

        const fieldTemplates = {
            AutoCounter: new Field('AutoCounter', '', [''], false, 'text'),
            Labels: new Field('Labels', '', [''], false, 'custom', {
                render: (it) => !!it.ColorTag && <ResourceTableTags data={it.ColorTag}/>, omitSort: true
            }),
            WorkOrderStatusID: new Field('WorkOrderStatusID', '', [''], false, 'select', {
                render: (it) => <WorkOrderStatusBadge
                    workOrderStatuses={workOrderStatuses}
                    statusID={it.WorkOrderStatusID}
                />
            }),
            TruckID: new Field('TruckID', '', TrailerID ? [''] : ['empty'], false, 'select-search', {
                render: (item) => {
                    if (!item) return null;

                    if (item.TruckID) {
                        return (
                            <CellButton
                                onClick={() => checkPerm(Resources.TrucksInfo, READ_PERM) && handleViewTruckClick(item.TruckID)}
                            >
                                {item.Truck}
                            </CellButton>
                        )
                    } else {
                        return (<span>{item.Truck}</span>)
                    }
                },
                addContainerClass: "col-span-6 col-start-1"
            }, {isClearable: true}),
            TrailerID: new Field('TrailerID', '', TruckID ? [''] : ['empty'], false, 'select-search', {
                render: (item) => {
                    if (!item) return null;

                    if (item.TrailerID) {
                        return (
                            <CellButton
                                onClick={() => checkPerm(Resources.Trailer, READ_PERM) && handleViewTrailerClick(item.TrailerID)}
                            >
                                {item.Trailer}
                            </CellButton>
                        )
                    } else {
                        return item.Trailer;
                    }
                },
                addContainerClass: "col-span-6"
            }, {isClearable: true}),
            AssignedVendorID: new Field('AssignedVendorID', '', [''], false, 'select-search', {}),
            TotalAmount: new Field('TotalAmount', '', [''], false, "float", {
                render: (it) => (
                    <Tippy
                        content={
                            <div className={"text-right"}>
                                <div>Labor: {genericMoneyFormatter(it.LaborAmount)}</div>
                                <div>Parts: {genericMoneyFormatter(it.PartsAmount)}</div>
                                <div>Shop supplies: {genericMoneyFormatter(it.MiscAmount)}</div>
                                <div>Misc. surcharge: {genericMoneyFormatter(it.SurchargeAmount)}</div>
                            </div>
                        }
                    >
                        <div className={"text-right"}>
                            {genericMoneyFormatter(it.TotalAmount)}
                        </div>
                    </Tippy>
                ),
                colFilter: true
            }, {icon: false}),
            OfficeID: new Field('OfficeID', '', ['empty'], false, 'select-search', {}),
            ContactGroupID: new Field('ContactGroupID', '', ['empty'], false, 'select-search', {}),
            IsAuthorized: new Field('IsAuthorized', '', [''], false, 'select', {
                render: (it) => <CheckboxCell value={it.IsAuthorized}/>,
                colFilter: true
            }, {addClass: "min-w-[9rem] form-control", hasPortal: true}),
            Notes: new Field('Notes', '', [], false, 'text', {
                render: (item) => (
                    <>{longTableColumn(item.Notes)}</>
                )
            })
        }

        return fillFieldsFromData(fieldTemplates, item)
    }

    /** Component Body
     ================================================================= */
    return (
        <React.Fragment>
            <Layout
                history={history}
                match={match}
                translate={translate}
            >
                <Page>
                    <PageHeader
                        title={translate("page_title.work_order_pay")}
                        afterTitle={
                            <>
                            </>
                        }
                    />

                    <ResourceList
                        fields={getFields()}
                        resource={resource}
                        isLoading={isLoading}
                        queryFields={queryFields}
                        setQueryFields={setQueryFields}
                        listPath={getListPath()}
                        dispatch={dispatch}
                        query={query}
                        onSelectRow={handleSelectRowClick}
                        onSelectAllClick={handleSelectAllClick}
                        selectedRows={selectedRows}
                        onRefreshTable={fetchData}
                        translate={translate}
                        selects={getSelects()}
                        actions={[
                            {
                                icon: Cog6ToothIcon,
                                title: translate("btn.process"),
                                hasPerm: checkPerm(Resources.BillingWorkOrders, CREATE_PERM),
                                action: handleProcessBillClick
                            }, {
                                icon: EyeIcon,
                                title: translate("btn.view_work_order"),
                                action: handleViewResourceClick
                            }
                        ]}
                        tableKey={"WorkOrderID"}
                        onRowClick={handleProcessBillClick}
                    />

                    <TableBulkActions
                        selectedRows={selectedRows}
                        tableKey="WorkOrderID"
                        fields={getFields()}
                        translate={translate}
                        options={tableOptions}
                        setSelectedRows={setSelectedRows}
                        onSelectAllClick={handleSelectAllClick}
                        footerContent={<div className="leading-9 px-4 font-bold">Total amount for selected entries:
                            ${roundedTotalAmount}</div>}
                    >
                        <div className='flex items-center h-8'>
                            <div className={classNames('flex divide-x-2 divide-primary-shade pr-2')}>
                            </div>

                            <div className='flex gap-x-1 pr-2'>


                                <div className='flex gap-x-1 px-2'>
                                    <TableTagManager
                                        rowKey={'WorkOrderID'}
                                        btnIconClass='w-5 h-5'
                                        customIcon={<ChevronUpSolidIcon className="w-5 h-5"/>}
                                        selectedRows={selectedRows}
                                        resourceName={Resources.ColorTagWorkOrders}
                                        piggyResourceName={getResourcePath()}
                                        query={query}
                                        onAfterTagRows={() => {
                                            setSelectedRows({})
                                        }}
                                        translate={translate}
                                    />
                                </div>

                            </div>
                        </div>
                    </TableBulkActions>
                </Page>
            </Layout>

            {isProcessBillModalOpen && (
                <WOPayProcessBill
                    show={isProcessBillModalOpen}
                    translate={translate}
                    selectedItem={selectedItem}
                    query={query}
                    onClose={() => setIsProcessBillModalOpen(false)}
                />
            )}
        </React.Fragment>
    )
}

/** Page methods
 ================================================================= */
const getPrimaryField = () => "WorkOrderID";
const getResourcePath = () => Resources.WorkOrdersPay;

const getListPath = () => getResourcePath() + "_list"

const getQueryFields = (translate) => {
    return (
        Object.assign(
            getDefaultQueryFields(getPrimaryField(), translate),
            {
                TruckID: new Field('TruckID', '', [], false, 'select-search', {hasActiveBadge: true}, {isClearable: true}),
                TrailerID: new Field('TrailerID', '', [], false, 'select-search', {hasActiveBadge: true}, {isClearable: true}),
                AssignedVendorID: new Field('AssignedVendorID', '', [], false, 'select-search', {hasActiveBadge: true}, {isClearable: true}),
            }
        )
    )
}


function getSelects() {
    return {
        AssignedVendorID: {
            api: 'api/' + Resources.Vendors,
            query: {},
            searchMap: (it) => ({
                label: it.LegalName,
                value: it.VendorID,
            })
        },
        TruckID: {
            api: 'api/' + Resources.Trucks,
            query: {
                NotSoldRetired: 1
            },
            customizeList: (list) => {
                return groupListBySCAC(list, 'Truck')
            }
        },
        TrailerID: {
            api: 'api/' + Resources.Trailers,
            query: {
                NotSoldRetired: 1
            },
            customizeList: (list) => {
                return groupListBySCAC(list, 'Trailer')
            }
        },
    }
}
